<template>
  <div
    v-if="element.type === 'placeholder'"
    class="placeholder-on-add-widget placeholder-on-add-widget--allowed m-4"
  />
  <div
    v-else
    :data-widget="JSON.stringify(element)"
    :data-widget-location="JSON.stringify({ rowId, columnId: columnData.id, widgetIndex: index })"
  >
    <div
      v-show="isChoosedForDrag"
    />
    <div
      v-show="!isChoosedForDrag"
      class="relative sb-col-item handle-widget"
      :class="{ 'sb-col-item--hovered': hovered || isActiveWidget }"
      @mouseover="onMouseOver"
      @mouseleave="onMouseLeave"
      @click="editWidget"
    >
      <div
        v-if="hovered"
        class="sb-title"
      >
        <span>{{ t('statsEditor.widget') }}</span>
      </div>
      <div
        v-if="hovered"
        class="sb-col-actions z-2"
      >
        <div class="flex flex-column">
          <div
            v-tooltip.right="t('statsEditor.actions.tooltip.widget.remove')"
            class="flex sb-action mb-1"
            @click.stop="removeWidget"
          >
            <i class="far fa-trash" />
          </div>
        </div>
      </div>
      <StatsWidgetDataRender
        v-if="element.type === 'StatsWidgetData'"
        :widget-data="element"
      />
      <StatsWidgetTitleRender
        v-else-if="element.type === 'StatsWidgetTitle'"
        :widget-data="element"
      />
      <StatsWidgetMenuRender
        v-else-if="element.type === 'StatsWidgetMenu'"
        :row-id="rowId"
        :column-id="columnData.id"
        :edit-mode="(selectedWidgetState !== null) && (rowId === selectedWidgetState.location.rowId && columnData.id === selectedWidgetState.location.columnId && index === selectedWidgetState.location.widgetIndex)
          && (selectedWidgetState.location.panelData === null)"
        :widget-data="element"
      />
      <StatsWidgetTabsRender
        v-else-if="element.type === 'StatsWidgetTabs'"
        :row-id="rowId"
        :column-id="columnData.id"
        :edit-mode="(selectedWidgetState !== null) && (rowId === selectedWidgetState.location.rowId && columnData.id === selectedWidgetState.location.columnId && index === selectedWidgetState.location.widgetIndex)
          && (selectedWidgetState.location.panelData === null)"
        :widget-data="element"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent,
  Ref, SetupContext, ref,
} from 'vue';

import Tooltip from 'primevue/tooltip';

import StatsWidgetTabsRender from '@/components/stats-builder/widgets/render/StatsWidgetTabsRender.vue';
import StatsWidgetMenuRender from '@/components/stats-builder/widgets/render/StatsWidgetMenuRender.vue';
import StatsWidgetDataRender from '@/components/stats-builder/widgets/render/StatsWidgetDataRender.vue';
import StatsWidgetTitleRender from '@/components/stats-builder/widgets/render/StatsWidgetTitleRender.vue';
import { StatsBuilderSelectedWidget, StatsBuilderChoosedWidgetForDrag } from '@/types/stats-editor-types';
import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'StatsBuilderColItem',

  components: {
    StatsWidgetDataRender,
    StatsWidgetMenuRender,
    StatsWidgetTabsRender,
    StatsWidgetTitleRender,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    element: {
      type: Object,
      required: true,
    },

    index: {
      type: Number,
      required: true,
    },

    rowId: {
      type: String,
      required: true,
    },

    columnData: {
      type: Object,
      required: true,
    },
  },

  emits: ['edit-widget', 'remove-widget'],

  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();
    const store = useStore();
    const selectedWidgetState: Ref<StatsBuilderSelectedWidget|null> = computed(() => store.getters['statsEditor/getSelectedWidget']);
    const choosedWidgetForDrag = computed<StatsBuilderChoosedWidgetForDrag | null>(() => store.getters['statsEditor/getChoosedWidgetForDrag']);

    const hovered = ref(false);

    const isActiveWidget = computed(() => {
      if (!selectedWidgetState.value) {
        return false;
      }
      const { location } = selectedWidgetState.value;
      if (location.rowId !== props.rowId || location.columnId !== props.columnData.id) {
        return false;
      }
      if (location.panelData) {
        return false;
      }
      return location.widgetIndex === props.index;
    });

    const isChoosedForDrag = computed(() => {
      if (!choosedWidgetForDrag.value) {
        return false;
      }
      return choosedWidgetForDrag.value.rowId === props.rowId
        && choosedWidgetForDrag.value.columnId === props.columnData.id && choosedWidgetForDrag.value.index === props.index && !choosedWidgetForDrag.value.panelData;
    });

    const editWidget = () => {
      emit('edit-widget', props.index);
    };

    const removeWidget = () => {
      emit('remove-widget', props.index);
    };

    const onMouseOver = () => {
      hovered.value = true;
    };
    const onMouseLeave = () => {
      hovered.value = false;
    };

    return {
      t,
      selectedWidgetState,
      hovered,
      isChoosedForDrag,
      isActiveWidget,

      editWidget,
      removeWidget,
      onMouseOver,
      onMouseLeave,
    };
  },

});
</script>

<style lang="scss">
  $brand-green: #94c840;
  $brand-green-dark: #36520f;

  #pageRowsList .sb-col-item {
    position: relative;
    border: 1px solid rgba(73, 80, 87, 0.12);
    min-height: 5rem;
    height: 100%; // Force la hauteur totale pour l'équilibrage des KPI
    display: flex; // Permet aux enfants de s'étendre
    flex-direction: column; // Empile verticalement
    border-radius: 0.75rem;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease;
  }

  #pageRowsList .sb-col-item--hovered {
    border-color: rgba($brand-green, 0.85);
    box-shadow: 0 18px 36px -28px rgba($brand-green-dark, 0.45), 0 0 0 1px rgba($brand-green, 0.4);
    background: linear-gradient(135deg, rgba($brand-green, 0.14), rgba($brand-green, 0.04));
    transform: translateY(-2px);
  }

  #pageRowsList .sb-col-actions {
    position: absolute;
    top: 0.85rem;
    left: 0.85rem;
    z-index: 1;

    .sb-action {
      cursor: pointer;
      padding: 0.55rem;
      background: #ffffff;
      border: 1px solid rgba($brand-green, 0.28);
      border-radius: 0.65rem;
      box-shadow: 0 12px 24px -20px rgba($brand-green-dark, 0.35);
      transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
      color: #2f3b1a;
    }

    .sb-action:hover {
      background: #ffffff;
      border-color: rgba($brand-green, 0.55);
      box-shadow: 0 16px 28px -18px rgba($brand-green-dark, 0.45);
      transform: translateY(-1px);
      color: #2a4312;
    }
  }

  #pageRowsList .sb-title {
    position: absolute;
    top: -1.35rem;
    left: 1rem;
    display: inline-flex;
    align-items: center;
    padding: 0.4rem 0.85rem 0.35rem;
    border-radius: 999px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(231, 247, 206, 0.92));
    border: 1px solid rgba($brand-green, 0.45);
    color: #314911;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    line-height: 1rem;
    box-shadow: 0 12px 28px -20px rgba($brand-green-dark, 0.45);
    z-index: 2;
  }
</style>
